import type { StateTree, TradeInComponent } from '@makemydeal/dr-dash-types';
import type { MultipleTradeInsState, SingleTradeInItem } from '../reducers/tradeInsReducer';
import { initialState } from '../reducers/tradeInsReducer';
import type { TradeInsScopedState } from '../reducers/tradeInsScopedReducer';
import { initialState as scopedInitialState } from '../reducers/tradeInsScopedReducer';
import type { TradeInState } from '../reducers/tradeInReducer';
import { createSelector } from 'reselect';
import type { ITradeIn, TradeEvaluation } from '@makemydeal/dr-platform-types';

export interface IncludedSingleTradeInItemWithIndex extends SingleTradeInItem {
    originalIndex: number;
}

export const getMultipleTradeInsState = createSelector(
    [(state: StateTree) => state.tradeIns],
    (tradeIns): MultipleTradeInsState => tradeIns || initialState
);

export const getTradeInsScopedState = createSelector(
    [(state: StateTree) => state.tradeInsScoped],
    (tradeInsScoped): TradeInsScopedState => tradeInsScoped || scopedInitialState
);

/**
 * Gets all trade-ins with merged global and scoped state.
 * Each trade-in's shouldIncludeTradeIn comes from the scoped state.
 */
export const getAllTradeIns = createSelector(
    [getMultipleTradeInsState, getTradeInsScopedState],
    (multiState, scopedState): SingleTradeInItem[] => {
        const items = multiState.items || [];
        return items.map((item, index) => ({
            ...item,
            tradeIn: {
                ...item.tradeIn,
                shouldIncludeTradeIn: scopedState.shouldIncludeTradeIns[index] ?? false
            }
        }));
    }
);

/**
 * Gets trade-in by index with merged global and scoped state.
 * Returns trade-in data from global state merged with shouldIncludeTradeIn from scoped state.
 * This matches the pattern used in single trade-in where getTradeIn merges global and scoped.
 */
export const getTradeInByIndex = createSelector(
    [getAllTradeIns, (_, tradeInIndex: number) => tradeInIndex],
    (allTradeIns, tradeInIndex): TradeInState => {
        // getAllTradeIns already merges shouldIncludeTradeIn from scoped state
        return (allTradeIns[tradeInIndex]?.tradeIn || {}) as TradeInState;
    }
);

export const getTradeInComponentByIndex = createSelector(
    [getAllTradeIns, (_, tradeInIndex: number) => tradeInIndex],
    (allTradeIns, tradeInIndex): TradeInComponent => (allTradeIns[tradeInIndex]?.tradeInComponent || {}) as TradeInComponent
);

export const getDoors =
    (tradeInIndex: number) =>
    (state: StateTree): string => {
        return getTradeInByIndex(state, tradeInIndex).doors || '';
    };

export const getLienholderFull = (tradeInIndex: number) =>
    createSelector([(state: StateTree) => getTradeInByIndex(state, tradeInIndex)], (tradeIn) => ({
        lienholder: tradeIn.lienHolder || '',
        lienholderAccountNumber: tradeIn.lienHolderAccountNumber || '',
        lienholderAddress: tradeIn.lienHolderAddress || {},
        lienholderPhoneNumber: tradeIn.lienHolderPhoneNumber || '',
        perDiemInterest: tradeIn.perDiemInterest || 0,
        goodThroughDate: tradeIn.goodThroughDate || ''
    }));

export const getTradeYear = (tradeInIndex: number) => (state: StateTree) => {
    const year = getTradeInByIndex(state, tradeInIndex).year;
    return year ? year.toString() : '';
};

export const getTradeMake = (tradeInIndex: number) => (state: StateTree) => {
    return getTradeInByIndex(state, tradeInIndex).make || '';
};

export const getTradeMakeId = (tradeInIndex: number) => (state: StateTree) => {
    return getTradeInByIndex(state, tradeInIndex).makeId || '';
};

export const getTradeModel =
    (tradeInIndex: number) =>
    (state: StateTree): string => {
        return getTradeInByIndex(state, tradeInIndex).model || '';
    };

export const getTradeTrim =
    (tradeInIndex: number) =>
    (state: StateTree): string => {
        return getTradeInByIndex(state, tradeInIndex).trim || '';
    };

export const getTradeMileage =
    (tradeInIndex: number) =>
    (state: StateTree): number => {
        return getTradeInByIndex(state, tradeInIndex).mileage || 0;
    };

export const getTradeStockNumber =
    (tradeInIndex: number) =>
    (state: StateTree): string => {
        return getTradeInByIndex(state, tradeInIndex).stockNumber || '';
    };

export const isTradeInCompletedFalsy = (state: StateTree, tradeInIndex: number): boolean | undefined =>
    getTradeInByIndex(state, tradeInIndex).isCompleted;

export const isTradeInCompleted =
    (tradeInIndex: number) =>
    (state: StateTree): boolean =>
        isTradeInCompletedFalsy(state, tradeInIndex) || false;

export const getShouldIncludeTradeIn =
    (tradeInIndex: number) =>
    (state: StateTree): boolean => {
        return getTradeInByIndex(state, tradeInIndex).shouldIncludeTradeIn as boolean;
    };

export const getDriveLine =
    (tradeInIndex: number) =>
    (state: StateTree): string => {
        return getTradeInByIndex(state, tradeInIndex).driveLine || '';
    };

export const getTradeValue =
    (tradeInIndex: number) =>
    (state: StateTree): number =>
        getTradeInByIndex(state, tradeInIndex).value || 0;

export const getTradeInAmountOwed =
    (tradeInIndex: number) =>
    (state: StateTree): number =>
        getTradeInByIndex(state, tradeInIndex).amountOwed || 0;

export const getTradeACV =
    (tradeInIndex: number) =>
    (state: StateTree): number =>
        getTradeInByIndex(state, tradeInIndex).acv || 0;

export const getTradeSource =
    (tradeInIndex: number) =>
    (state: StateTree): string =>
        getTradeInByIndex(state, tradeInIndex).source ?? '';

export const getIsBuyerRegisteredOwner =
    (tradeInIndex: number) =>
    (state: StateTree): boolean =>
        getTradeInByIndex(state, tradeInIndex).isBuyerRegisteredOwner ?? true;

export const getIsSellingVehicleRegisteredInSameState =
    (tradeInIndex: number) =>
    (state: StateTree): boolean =>
        getTradeInByIndex(state, tradeInIndex).isSellingVehicleRegisteredInSameState ?? true;

export const getVin =
    (tradeInIndex: number) =>
    (state: StateTree): string =>
        getTradeInByIndex(state, tradeInIndex).vin?.toUpperCase() || '';

export const getTradeOwnershipType =
    (tradeInIndex: number) =>
    (state: StateTree): string =>
        getTradeInByIndex(state, tradeInIndex).ownershipType || '';

export const getYear =
    (tradeInIndex: number) =>
    (state: StateTree): string => {
        return getTradeInByIndex(state, tradeInIndex).year?.toString() || '';
    };

export const getMake =
    (tradeInIndex: number) =>
    (state: StateTree): string => {
        return getTradeInByIndex(state, tradeInIndex).make || '';
    };

export const getModel =
    (tradeInIndex: number) =>
    (state: StateTree): string => {
        return getTradeInByIndex(state, tradeInIndex).model || '';
    };

export const getTrim =
    (tradeInIndex: number) =>
    (state: StateTree): string => {
        return getTradeInByIndex(state, tradeInIndex).trim || '';
    };

export const getExteriorColor =
    (tradeInIndex: number) =>
    (state: StateTree): string => {
        return getTradeInByIndex(state, tradeInIndex).color || '';
    };

export const getCondition =
    (tradeInIndex: number) =>
    (state: StateTree): string => {
        return getTradeInByIndex(state, tradeInIndex).condition || '';
    };

export const getEngine =
    (tradeInIndex: number) =>
    (state: StateTree): string => {
        return getTradeInByIndex(state, tradeInIndex).engine || '';
    };

export const getTransmission =
    (tradeInIndex: number) =>
    (state: StateTree): string => {
        return getTradeInByIndex(state, tradeInIndex).transmission || '';
    };

export const getBodyStyle =
    (tradeInIndex: number) =>
    (state: StateTree): string => {
        return getTradeInByIndex(state, tradeInIndex).bodyStyle || '';
    };

export const getInteriorColor =
    (tradeInIndex: number) =>
    (state: StateTree): string => {
        return getTradeInByIndex(state, tradeInIndex).interiorColor || '';
    };

export const getOwnershipType =
    (tradeInIndex: number) =>
    (state: StateTree): string => {
        return getTradeInByIndex(state, tradeInIndex).ownershipType || '';
    };

export const hasMultipleVin =
    (tradeInIndex: number) =>
    (state: StateTree): boolean => {
        return getTradeInByIndex(state, tradeInIndex).hasMultipleVin || false;
    };

export const multipleVinOptions = (tradeInIndex: number) =>
    createSelector([(state: StateTree) => getTradeInByIndex(state, tradeInIndex)], (tradeIn) => tradeIn.multipleVinOptions || []);

export const getTradeInComponentVehicle = (tradeInIndex: number) =>
    createSelector([(state: StateTree) => getTradeInComponentByIndex(state, tradeInIndex)], (component) => component.vehicle || {});

export const getTradeInComponentYears = (tradeInIndex: number) =>
    createSelector(
        [(state: StateTree) => getTradeInComponentByIndex(state, tradeInIndex)],
        (component) => component.lookups?.years || { isLoading: false, options: [] }
    );

export const vinHasBeenDecoded =
    (tradeInIndex: number) =>
    (state: StateTree): boolean => {
        return getTradeInByIndex(state, tradeInIndex).vinHasBeenDecoded || false;
    };

//TODO update the same
export const getTradeInComponentMakes = (tradeInIndex: number) =>
    createSelector(
        [(state: StateTree) => getTradeInComponentByIndex(state, tradeInIndex)],
        (component) => component.lookups?.makes || { isLoading: false, options: [] }
    );

export const getTradeModelId =
    (tradeInIndex: number) =>
    (state: StateTree): string => {
        return getTradeInByIndex(state, tradeInIndex).modelId || '';
    };

export const getTradeInComponentModels = (tradeInIndex: number) =>
    createSelector(
        [(state: StateTree) => getTradeInComponentByIndex(state, tradeInIndex)],
        (component) => component.lookups?.models || { isLoading: false, options: [] }
    );

export const getTradeTrimDecodedOptions = (tradeInIndex: number) =>
    createSelector([(state: StateTree) => getTradeInByIndex(state, tradeInIndex)], (tradeIn) => tradeIn.trimDecodedOptions || []);

export const getTradeTrimId =
    (tradeInIndex: number) =>
    (state: StateTree): string => {
        return getTradeInByIndex(state, tradeInIndex).trimId || '';
    };

export const getTradeInComponentTrims = (tradeInIndex: number) =>
    createSelector(
        [(state: StateTree) => getTradeInComponentByIndex(state, tradeInIndex)],
        (component) => component.lookups?.trims || { isLoading: false, options: [] }
    );

export const isIncludedTradeIn = (tradeInItem: SingleTradeInItem): boolean =>
    !tradeInItem.isDeleted && !!tradeInItem.tradeIn.shouldIncludeTradeIn;

export const getIncludedTradeIns = (state: StateTree): SingleTradeInItem[] => {
    const allTradeIns = getAllTradeIns(state);
    return allTradeIns.filter(isIncludedTradeIn);
};

/**
 * Calculates trade-in summary from current state.
 * Filters by non-deleted AND shouldIncludeTradeIn (from scoped state).
 */
export const getTradeInSummary = createSelector([getIncludedTradeIns], (includedTradeIns) => {
    const totalAllowance = includedTradeIns.reduce((sum, item) => sum + (item.tradeIn.value || 0), 0);
    const totalAcv = includedTradeIns.reduce((sum, item) => sum + (item.tradeIn.acv || 0), 0);
    const totalPayoff = includedTradeIns.reduce((sum, item) => sum + (item.tradeIn.owed || 0), 0);
    const totalTradeInTaxReduction = includedTradeIns.reduce((sum, item) => sum + (item.tradeIn.tradeInTaxReduction || 0), 0);
    const netTrade = totalAllowance - totalPayoff;

    return {
        netTrade,
        totalAcv,
        totalAllowance,
        totalPayoff,
        totalTradeInTaxReduction
    };
});

export const getIncludedTradeInsTotalValue = (state: StateTree): number => {
    return getTradeInSummary(state).totalAllowance;
};

export const getIncludedTradeInsTotalAmountOwed = (state: StateTree): number => {
    return getTradeInSummary(state).totalPayoff;
};

export const hasSingleIncludedTradeIn = (state: StateTree): boolean => {
    const activeIncludedTradeIns = getIncludedTradeIns(state);
    return activeIncludedTradeIns.length === 1;
};

export const getTotalNetTradeValueForIncludedActiveTradeIns = (state: StateTree): number => {
    return getTradeInSummary(state).netTrade;
};

export const getActiveTradeIns = createSelector([getAllTradeIns], (allTradeIns): TradeInState[] =>
    allTradeIns.filter(({ isDeleted }) => !isDeleted).map(({ tradeIn }) => tradeIn)
);

export const isTradeInDeleted = (tradeInIndex: number) => (state: StateTree) => {
    return getAllTradeIns(state)[tradeInIndex]?.isDeleted || false;
};

export const getIncludedTradeInItems = (state: StateTree) => {
    return getIncludedTradeIns(state).map(({ tradeIn }, index) => ({ ...tradeIn, callerItemId: index + 1 }));
};

export const isTradeInsArray = (_state: StateTree, tradeIns: ITradeIn[]): boolean => {
    return Array.isArray(tradeIns);
};

/**
 * Gets included trade-ins with their original indices preserved from before filtering
 */
export const getIncludedTradeInsWithOriginalIndexes = (state: StateTree): IncludedSingleTradeInItemWithIndex[] => {
    const allTradeIns = getAllTradeIns(state);
    return allTradeIns.map((tradeInItem, index) => ({ ...tradeInItem, originalIndex: index })).filter(isIncludedTradeIn);
};

/**
 * Gets included trade-ins normalized for validation: each entry has lienholder address fields and the original index.
 */
export const getNormalizedTradeInsForValidation = (state: StateTree) => {
    const includedTradeIns = getIncludedTradeInsWithOriginalIndexes(state);
    return includedTradeIns.map((trade) => {
        const lienholderAddress = trade.tradeIn.lienHolderAddress || {};
        return {
            lienholder: {
                accountNumber: trade.tradeIn.lienHolderAccountNumber || '',
                streetAddress: lienholderAddress.address,
                city: lienholderAddress.city,
                state: lienholderAddress.stateProvince,
                zip: lienholderAddress.postalCode
            },
            index: trade.originalIndex
        };
    });
};

/**
 * Gets the original index of the first included trade-in, fallback to 0 if none exist
 */

export const getFirstIncludedTradeInIndex = (state: StateTree): number => {
    const includedTradeIns = getIncludedTradeInsWithOriginalIndexes(state);
    return includedTradeIns.length > 0 ? includedTradeIns[0].originalIndex : -1;
};

export const getAllTradeInVins = (state: StateTree): string[] => {
    const allTradeIns = getAllTradeIns(state);
    return allTradeIns.map((tradeInItem) => tradeInItem.tradeIn.vin || '');
};

export const getTradeReconData = (state: StateTree): TradeEvaluation[] => {
    const allTradeIns = getAllTradeIns(state);

    return allTradeIns.map((tradeInItem) => {
        const tradeIn = tradeInItem.tradeIn;
        return {
            vehicleName: [tradeIn.year, tradeIn.make, tradeIn.model, tradeIn.trim].filter(Boolean).join(' '),
            tradeAllowance: tradeIn.value || 0,
            tradePayoff: tradeIn.amountOwed || 0,
            tradeReconditionDetails: tradeIn.tradeReconditionDetails || null
        };
    });
};
